"use client";

import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useState } from "react";
import type { DateRange } from "react-day-picker";
import { Button } from "./button";
import { Calendar } from "./calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

export interface DateRangePreset {
  label: string;
  range: DateRange;
}

export interface DateRangePickerProps {
  captionLayout?: "dropdown" | "label";
  className?: string;
  numberOfMonths?: 1 | 2;
  onChange?: (range: DateRange | undefined) => void;
  placeholder?: string;
  presets?: DateRangePreset[];
  value?: DateRange;
}

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

export function DateRangePicker({
  value,
  onChange,
  placeholder = "Pick a date range",
  numberOfMonths = 2,
  captionLayout = "dropdown",
  className,
  presets,
}: DateRangePickerProps) {
  const [internalRange, setInternalRange] = useState<DateRange | undefined>(
    value
  );

  const range = value ?? internalRange;

  const handleSelect = (selected: DateRange | undefined) => {
    if (onChange) {
      onChange(selected);
    } else {
      setInternalRange(selected);
    }
  };

  const isActivePreset = (preset: DateRangePreset) => {
    if (!(range?.from && preset.range.from)) {
      return false;
    }
    const fromMatch = sameDay(range.from, preset.range.from);
    const toMatch =
      !(range.to || preset.range.to) ||
      (range.to != null &&
        preset.range.to != null &&
        sameDay(range.to, preset.range.to));
    return fromMatch && (toMatch as boolean);
  };

  const getDisplay = () => {
    if (!range?.from) {
      return <span>{placeholder}</span>;
    }
    if (range.to) {
      return (
        <>
          {format(range.from, "LLL dd, y")} - {format(range.to, "LLL dd, y")}
        </>
      );
    }
    return format(range.from, "LLL dd, y");
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button className={className} variant="outline">
          <CalendarIcon />
          {getDisplay()}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="flex w-auto p-0">
        {presets && presets.length > 0 && (
          <div className="flex flex-col gap-1 border-r p-2">
            {presets.map((preset) => (
              <Button
                className="justify-start"
                key={preset.label}
                onClick={() => handleSelect(preset.range)}
                size="sm"
                variant={isActivePreset(preset) ? "secondary" : "ghost"}
              >
                {preset.label}
              </Button>
            ))}
            {range?.from && (
              <Button
                className="justify-start text-muted-foreground"
                onClick={() => handleSelect(undefined)}
                size="sm"
                variant="ghost"
              >
                Clear
              </Button>
            )}
          </div>
        )}
        <Calendar
          captionLayout={captionLayout}
          defaultMonth={range?.from}
          mode="range"
          numberOfMonths={numberOfMonths}
          onSelect={handleSelect}
          selected={range}
        />
      </PopoverContent>
    </Popover>
  );
}
